import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ArbolPendientes, fmtMonto } from '../agrupadores/ArbolPendientes';
import { nodoOtrosTramites } from '../../utils/agrupadores';
import type { CobroPendiente, ItemCobroPendiente, Moneda, NodoCobros } from '../../types';

/** A quién se le manda el recordatorio de WhatsApp: el saldo de un trámite en una moneda. */
export type RecordatorioCobro = Pick<CobroPendiente, 'id' | 'origen' | 'tramiteId' | 'otroTramiteId' | 'moneda'>;

interface Props {
  nodos: NodoCobros[];
  /** Cobros pendientes de otros trámites: no tienen agrupador, van en un nodo aparte. */
  otrosTramites?: CobroPendiente[];
  moneda: Moneda | 'todas';
  /** Con búsqueda activa conviene ver todo abierto. */
  expandirTodo?: boolean;
  canCobrar: boolean;
  canWhatsApp?: boolean;
  disabled?: boolean;
  onCobrarNodo: (nodo: NodoCobros, moneda: Moneda) => void;
  onCobrarItem: (item: ItemCobroPendiente) => void;
  onCobrarOtroTramite?: (cobro: CobroPendiente) => void;
  onWhatsApp?: (recordatorio: RecordatorioCobro) => void;
}

const VACIO = 'Sin cobros para los filtros seleccionados.';

export function ArbolCobrosPendientes({
  nodos,
  otrosTramites = [],
  moneda,
  expandirTodo = false,
  canCobrar,
  canWhatsApp = false,
  disabled = false,
  onCobrarNodo,
  onCobrarItem,
  onCobrarOtroTramite,
  onWhatsApp,
}: Props) {
  if (nodos.length === 0 && otrosTramites.length === 0) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{VACIO}</p>;
  }

  const whatsApp = canWhatsApp ? onWhatsApp : undefined;

  return (
    <>
      {nodos.length > 0 && (
        <ArbolPendientes
          nodos={nodos}
          moneda={moneda}
          expandirTodo={expandirTodo}
          verbo="Cobrar"
          vacio={VACIO}
          canAccionar={canCobrar}
          disabled={disabled}
          onAccionNodo={onCobrarNodo}
          renderItem={(item, profundidad) => (
            <ItemRow
              profundidad={profundidad}
              tramite={
                <Link
                  to="/dashboard"
                  state={{ tramite: { id: item.tramiteId } }}
                  className="mr-1.5 font-medium text-primary hover:underline"
                >
                  #{item.tramiteId}
                </Link>
              }
              nombre={item.tramitePropiedad ? `${item.tramitePropiedad} - ${item.itemNombre}` : item.itemNombre}
              cobrarLabel={`Cobrar ${item.itemNombre}`}
              clienteNombre={item.clienteNombre}
              fecha={item.fecha}
              saldo={item.saldoRestante}
              cobrado={item.totalCobrado}
              moneda={item.moneda}
              disabled={disabled}
              onCobrar={canCobrar ? () => onCobrarItem(item) : undefined}
              whatsAppLabel={`Enviar recordatorio de pago del trámite #${item.tramiteId} por WhatsApp`}
              onWhatsApp={whatsApp && (() => whatsApp({
                id: `ESC:${item.tramiteId}:${item.moneda}`,
                origen: 'escritura',
                tramiteId: item.tramiteId,
                moneda: item.moneda,
              }))}
            />
          )}
        />
      )}

      {otrosTramites.length > 0 && (
        <div className="border-t border-gray-100">
          <ArbolPendientes
            nodos={[nodoOtrosTramites(otrosTramites)]}
            moneda={moneda}
            expandirTodo={expandirTodo}
            verbo="Cobrar"
            vacio={VACIO}
            canAccionar={false}
            onAccionNodo={() => {}}
            renderItem={(cobro, profundidad) => (
              <ItemRow
                profundidad={profundidad}
                tramite={<span className="mr-1.5 font-medium text-gray-700">{cobro.otroTramiteId}</span>}
                nombre={cobro.itemNombre}
                cobrarLabel={`Cobrar ${cobro.otroTramiteId} ${cobro.itemNombre}`}
                clienteNombre={cobro.clienteNombre}
                fecha={cobro.fecha}
                saldo={cobro.saldoRestante}
                cobrado={cobro.totalCobrado}
                moneda={cobro.moneda}
                disabled={disabled}
                onCobrar={canCobrar && onCobrarOtroTramite ? () => onCobrarOtroTramite(cobro) : undefined}
                whatsAppLabel={`Enviar recordatorio de pago de ${cobro.otroTramiteId} por WhatsApp`}
                onWhatsApp={whatsApp && (() => whatsApp(cobro))}
              />
            )}
          />
        </div>
      )}
    </>
  );
}

function ItemRow({
  profundidad, tramite, nombre, cobrarLabel, clienteNombre, fecha, saldo, cobrado, moneda, disabled, onCobrar, whatsAppLabel, onWhatsApp,
}: {
  profundidad: number;
  tramite: ReactNode;
  nombre: string;
  cobrarLabel: string;
  clienteNombre: string;
  fecha: string;
  saldo: number;
  cobrado: number;
  moneda: Moneda;
  disabled: boolean;
  onCobrar?: () => void;
  whatsAppLabel: string;
  onWhatsApp?: () => void;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-dashed border-gray-100 bg-slate-50/50 py-2 pr-4"
      style={{ paddingLeft: `${40 + profundidad * 20}px` }}
    >
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm text-gray-900">
          {tramite}
          {nombre}
        </p>
        <p className="text-xs text-muted-foreground">
          {clienteNombre ? `${clienteNombre} · ` : ''}{fecha}
        </p>
      </div>
      <div className="text-right leading-tight">
        <p className="text-sm font-semibold tabular-nums text-slate-600">{fmtMonto(saldo, moneda)}</p>
        {cobrado > 0 && (
          <p className="text-[11px] tabular-nums text-blue-600">cobrado {fmtMonto(cobrado, moneda)}</p>
        )}
      </div>
      {(onCobrar || onWhatsApp) && (
        <div className="flex items-center gap-1.5">
          {onCobrar && (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled}
              className="h-7 rounded-lg px-2.5 text-xs"
              onClick={onCobrar}
              aria-label={cobrarLabel}
            >
              Cobrar
            </Button>
          )}
          {onWhatsApp && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-lg px-2"
              onClick={onWhatsApp}
              title={whatsAppLabel}
              aria-label={whatsAppLabel}
            >
              <MessageCircle className="size-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
